import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'gatsby'
import { useTheme } from '@emotion/react'
import { tealBrightTeal } from 'chaoskit/src/assets/styles/utility/gradient'
import {
  Container,
  Section,
  Alert,
  SectionTitle,
} from 'chaoskit/src/components'
import { text } from 'chaoskit/src/assets/styles/utility'
import useInterval from 'react-use/lib/useInterval'
import qs from 'qs'

import BaseLayout from '~layouts/BaseLayout'
import HelmetSEO from '~partials/global/HelmetSEO'
import Hero, { HeroContent, HeroTitle, HeroTitleSub } from '~partials/Hero'
import Data from '~components/Data'
import {
  createTimeObject,
  CounterWrapper,
  CounterGroup,
  CounterDigit,
} from '~components/Counter'
import WeeklyLiveDemoForm from '~partials/weekly-live-demo/WeeklyLiveDemoForm'
import NodeTypeTracker from '~partials/global/NodeTypeTracker'
import Image from '~components/Image'
import dateFn from '~helpers/dateFn'

/**
 * This recently started to not behave correctly with adding days to a date, so we're storing next week's in a var eagerly to get around the issue
 */
function getDateOffset() {
  const now = dateFn().tz('America/New_York')
  const eventTimeUpcoming = now.startOf('day').day(3).add(14, 'hour')
  const eventTimeNextWeek = eventTimeUpcoming.add(1, 'week')

  if (eventTimeUpcoming.diff(now, 'second') <= 0) {
    return eventTimeNextWeek.diff(now, 'second')
  }

  return eventTimeUpcoming.diff(now, 'second')
}

const Counter = () => {
  const theme = useTheme()

  const [delay, setDelay] = useState(1000)
  const [time, setTime] = useState(getDateOffset())
  const [timeObject, setTimeObject] = useState(createTimeObject(time))

  const adjustClock = () => {
    const nextTime = time - 1
    setTime(nextTime)

    setTimeObject(createTimeObject(nextTime))
  }

  useInterval(() => {
    adjustClock()
  }, delay)

  useEffect(() => {
    // Stop when finished; will reset on refresh
    if (time === 0) {
      setDelay(null)
    }
  }, [time])

  return (
    <CounterWrapper
      groupCount={4}
      css={{
        [theme.mq.small__max]: {
          justifyContent: 'center',
        },
      }}
    >
      <CounterGroup size="small" label="Days">
        <CounterDigit size="small" digit={timeObject.days.split('')[0]} />
        <CounterDigit
          size="small"
          digit={
            timeObject.days.split('')[1] === '7'
              ? 0
              : timeObject.days.split('')[1]
          }
        />
      </CounterGroup>
      <CounterGroup size="small" label="Hours">
        <CounterDigit size="small" digit={timeObject.hours.split('')[0]} />
        <CounterDigit size="small" digit={timeObject.hours.split('')[1]} />
      </CounterGroup>
      <CounterGroup size="small" label="Minutes">
        <CounterDigit size="small" digit={timeObject.minutes.split('')[0]} />
        <CounterDigit size="small" digit={timeObject.minutes.split('')[1]} />
      </CounterGroup>
      <CounterGroup size="small" label="Seconds">
        <CounterDigit size="small" digit={timeObject.seconds.split('')[0]} />
        <CounterDigit size="small" digit={timeObject.seconds.split('')[1]} />
      </CounterGroup>
    </CounterWrapper>
  )
}

const WeeklyLiveDemo = ({
  data: {
    file: {
      childMdx: { frontmatter: pageData },
    },
  },
  location,
}) => {
  const theme = useTheme()

  const [showCancelledBanner, setShowCancelledBanner] = useState(false)

  useEffect(() => {
    const queryParams = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    })

    if (queryParams.status === 'cancelled') {
      setShowCancelledBanner(true)
    }
  }, [location.search])

  return (
    <BaseLayout
      headerProps={{ sticky: false, showNav: false }}
      footerProps={{ hideCTA: true }}
    >
      <HelmetSEO
        title={pageData.title}
        description={pageData.description}
        image={pageData.socialMediaImage}
      />
      <NodeTypeTracker nodeType="webinars" />
      <Hero contrast={false} css={[tealBrightTeal(theme)]}>
        <HeroContent>
          <Container>
            {showCancelledBanner && (
              <Alert type="danger">
                This session has been cancelled. Please join an upcoming live
                demo instead.
              </Alert>
            )}
            <div
              css={{
                display: 'grid',
                gap: theme.space.base,

                [theme.mq.medium]: {
                  gap: theme.space.xlarge,
                  gridTemplateColumns: 'repeat(2, 1fr)',
                },
              }}
            >
              <div className="u-contrast">
                <div
                  css={{
                    [theme.mq.medium]: {
                      position: 'sticky',
                      top: theme.space.base,
                    },
                  }}
                >
                  <HeroTitle as="h2" css={{ marginTop: 0 }}>
                    <Data>{pageData.hero.heading}</Data>
                  </HeroTitle>
                  <HeroTitleSub>
                    <Data markdown>{pageData.hero.content}</Data>
                  </HeroTitleSub>
                  <div
                    css={[
                      text.heading(theme),
                      {
                        fontSize: theme.fontSize.large,
                        marginTop: theme.space.large,
                      },
                    ]}
                  >
                    Every Wednesday at 2p ET / 11a PT
                  </div>

                  <h5 css={{ color: `${theme.contrast.muted} !important` }}>
                    Our next demo starts in
                  </h5>
                  <Counter />
                </div>
              </div>
              <WeeklyLiveDemoForm />
            </div>
          </Container>
        </HeroContent>
      </Hero>
      <Section>
        <Container size="small">
          <div
            css={{
              display: 'grid',
              gap: theme.space.base,

              [theme.mq.medium]: {
                gridTemplateColumns: '1fr 3fr',
              },
            }}
          >
            <div css={{ textAlign: 'center' }}>
              <Image src="https://res.cloudinary.com/gremlin/image/upload/v1588080150/Webinars/tshirt.png" />
            </div>
            <div>
              <SectionTitle
                align={{ medium: 'left' }}
                title="Attend our next live demo, get a free Gremlin t-shirt"
                sub="By joining our live demo, you could get a free Gremlin t-shirt! Just be sure to fill out the survey."
              />
              <Alert type="primary" title="Note">
                At this time we are unable to ship t-shirts outside of the
                United States.
              </Alert>
            </div>
          </div>
        </Container>
      </Section>
    </BaseLayout>
  )
}

WeeklyLiveDemo.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
}

export default WeeklyLiveDemo

export const pageQuery = graphql`
  query WeeklyLiveDemoPageData {
    file(
      name: { eq: "weekly-live-demo" }
      absolutePath: { regex: "/src/data/" }
    ) {
      childMdx {
        frontmatter {
          title
          description
          socialMediaImage
          hero {
            heading
            content
          }
          eventSessions {
            date
            time
            id
          }
        }
      }
    }
  }
`
